<template>
  <el-card v-loading="isLoading" shadow="always">
    <template #header>
      <el-row type="flex" justify="space-between" align="center">
        <el-col :md="8" :xs="24">
          <el-row type="flex" justify="start">
            <h4>{{ $route.name }}</h4>
          </el-row>
        </el-col>
        <el-col :md="16" :xs="24">
          <el-row type="flex" justify="end" align="center">
            <div>
              <el-select
                v-model="selectedMonth"
                size="medium"
                class="mr-6"
                placeholder="Mes"
                @change="handleMonthChange"
              >
                <el-option
                  v-for="option in monthOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                ></el-option>
              </el-select>
            </div>
            <el-button
              type="primary"
              class="mb-0"
              icon="el-icon-download"
              :loading="isLoadingDownloadButton"
              size="medium"
              @click="downloadReport"
            ></el-button>
          </el-row>
        </el-col>
      </el-row>
    </template>
    <template v-if="employeeSections.length">
      <div
        v-for="section in employeeSections"
        :key="section.employeeUid || section.employeeName"
        class="employee-section"
      >
        <h2 class="el-table__subheader_name">{{ section.employeeName }}</h2>
        <el-table
          stripe
          :data="section.rows"
          style="width: 100%; z-index: 0"
          :cell-style="() => 'text-align:center;'"
          :row-key="(row) => row.id"
        >
          <el-table-column
            prop="description"
            label="descricao"
          ></el-table-column>
          <el-table-column
            prop="realizedAt"
            label="realizado em"
          ></el-table-column>
          <el-table-column label="status">
            <template #default="{ row }">
              <el-tag
                :type="row.statusTag"
                :class="row.statusClass"
                effect="plain"
                size="small"
              >
                {{ row.statusLabel }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
            prop="amountFormatted"
            label="valor"
          ></el-table-column>
        </el-table>
        <el-row class="employee-total-row">
          <el-col :md="22" :xs="16">
            <p class="summary summary-black">Total do funcionario:</p>
          </el-col>
          <el-col :md="2" :xs="8">
            <p class="summary">{{ section.totalAmountFormatted }}</p>
          </el-col>
        </el-row>
      </div>
      <el-row class="overall-total-row">
        <el-col :md="22" :xs="16">
          <p class="summary summary-black">Total geral:</p>
        </el-col>
        <el-col :md="2" :xs="8">
          <p class="summary">{{ totalAmountFormatted }}</p>
        </el-col>
      </el-row>
    </template>
    <el-empty
      v-else-if="!isLoading && !hasError"
      description="Nenhum debito encontrado."
    />
    <el-alert
      v-if="hasError"
      type="error"
      show-icon
      title="Nao foi possivel carregar os dados."
      style="margin-top: 16px"
    ></el-alert>
  </el-card>
</template>

<script>
import EmployeeService from "../services/employees";
import { ElNotification } from "element-plus";

const MONTH_LABELS = [
  "Janeiro",
  "Fevereiro",
  "Marco",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const createMonthOptions = (count = 12) => {
  const options = [];
  const reference = new Date();
  for (let index = 0; index < count; index += 1) {
    const monthDate = new Date(
      reference.getFullYear(),
      reference.getMonth() - index,
      1
    );
    const value = `${monthDate.getFullYear()}-${String(
      monthDate.getMonth() + 1
    ).padStart(2, "0")}`;
    const label = `${
      MONTH_LABELS[monthDate.getMonth()]
    } / ${monthDate.getFullYear()}`;
    options.push({ value, label });
  }
  return options;
};

const normalizeCollection = (collection) => {
  if (!collection) return [];
  if (Array.isArray(collection)) return collection;
  if (Array.isArray(collection.data)) return collection.data;
  if (Array.isArray(collection.rows)) return collection.rows;
  return [];
};

const escapeCsvValue = (value) => {
  if (value === null || value === undefined) return "";
  const stringValue = String(value).replace(/"/g, '""');
  return `"${stringValue}"`;
};

export default {
  name: "EmployeeDebtsReport",
  data() {
    const monthOptions = createMonthOptions();
    return {
      isLoading: false,
      hasError: false,
      isLoadingDownloadButton: false,
      monthOptions,
      selectedMonth: monthOptions[0]?.value || null,
      employeesDebts: [],
      currencyFormatter: new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }),
    };
  },
  computed: {
    employeeSections() {
      if (this.hasError || !this.selectedMonth) return [];
      const [year, month] = this.selectedMonth
        .split("-")
        .map((part) => Number(part));
      if (!year || !month) return [];

      return this.employeesDebts
        .map(({ employee, debts }) => {
          const cpfDigits = this.normalizeDigits(
            employee?.docs?.cpf || employee?.cpf
          );
          const rows = (debts || []).map((debt) => {
            const amount = Number(debt?.amount) || 0;
            const statusMeta = this.getStatusMeta(debt?.status);
            return {
              id:
                debt?.uid ||
                `${employee?.uid || "unknown"}-${debt?.realized_at || ""}`,
              description: debt?.obs || "",
              realizedAt: this.formatDate(debt?.realized_at),
              amount,
              amountFormatted: this.formatCurrency(amount),
              status: debt?.status || "opened",
              statusLabel: statusMeta.label,
              statusTag: statusMeta.tag,
              statusClass: statusMeta.className,
              isOpen: statusMeta.isOpen,
            };
          });

          const totalAmount = rows.reduce(
            (total, row) => total + (row.isOpen ? row.amount : 0),
            0
          );

          return {
            employeeUid: employee?.uid,
            employeeName: employee?.name || "-",
            employeeCpfRaw: cpfDigits,
            employeeCpf: cpfDigits ? this.formatCpf(cpfDigits) : "-",
            rows,
            totalAmount,
            totalAmountFormatted: this.formatCurrency(totalAmount),
          };
        })
        .filter((section) => section.rows.length);
    },
    totalAmount() {
      return this.employeeSections.reduce(
        (total, section) => total + section.totalAmount,
        0
      );
    },
    totalAmountFormatted() {
      return this.formatCurrency(this.totalAmount);
    },
  },
  created() {
    this.loadReport();
  },
  methods: {
    async loadReport() {
      this.isLoading = true;
      this.hasError = false;
      try {
        const { employees, error } = await EmployeeService().index({
          is_active: true,
        });
        if (error) throw error;
        const employeeList = normalizeCollection(employees);

        const debtsPromise = employeeList.map(async (employee) => {
          const { debts, error: debtsError } = await EmployeeService(
            employee.uid
          )
            .Debts()
            .index({
              month: Number(this.selectedMonth?.split("-")[1]) - 1,
              year: this.selectedMonth?.split("-")[0],
            });
          if (debtsError) throw debtsError;
          return {
            employee,
            debts: normalizeCollection(debts),
          };
        });

        const debtsByEmployee = await Promise.all(debtsPromise);

        this.employeesDebts = debtsByEmployee;
      } catch (error) {
        this.hasError = true;
        ElNotification.error({
          title: "Erro ao carregar relatorio",
          message: error?.message || "Tente novamente mais tarde.",
          position: "bottom-right",
        });
      } finally {
        this.isLoading = false;
      }
    },
    formatCurrency(value) {
      const numericValue = Number(value);
      return Number.isFinite(numericValue)
        ? this.currencyFormatter.format(numericValue)
        : this.currencyFormatter.format(0);
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
    },
    formatCpf(value) {
      if (!value) return "-";
      const digits = `${value}`.replace(/\D/g, "").padStart(11, "0").slice(-11);
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    },
    normalizeDigits(value) {
      if (!value) return "";
      return `${value}`.replace(/\D/g, "");
    },
    getStatusMeta(status) {
      const normalized = (status || "opened").toString().toLowerCase();
      if (normalized === "paid") {
        return {
          label: "Pago",
          tag: "success",
          className: "status-tag status-tag-paid",
          isOpen: false,
        };
      }
      if (normalized === "canceled" || normalized === "cancelled") {
        return {
          label: "Cancelado",
          tag: "warning",
          className: "status-tag status-tag-canceled",
          isOpen: false,
        };
      }
      if (
        normalized === "opened" ||
        normalized === "open" ||
        normalized === "created"
      ) {
        return {
          label: "Aberto",
          tag: "info",
          className: "status-tag status-tag-opened",
          isOpen: true,
        };
      }
      return {
        label: "Desconhecido",
        tag: "info",
        className: "status-tag",
        isOpen: false,
      };
    },
    debtMatchesMonth(debt, year, month) {
      if (!debt?.realized_at) return false;
      const date = new Date(debt.realized_at);
      if (Number.isNaN(date.getTime())) return false;
      return date.getFullYear() === year && date.getMonth() + 1 === month;
    },
    handleMonthChange() {
      // Computed properties react to selectedMonth automatically.
    },
    downloadReport() {
      const rows = [];
      this.employeeSections.forEach((section) => {
        section.rows.forEach((row) => {
          rows.push({
            employeeName: section.employeeName,
            employeeCpf: section.employeeCpf,
            description: row.description,
            realizedAt: row.realizedAt,
            status: row.statusLabel,
            amount: row.amountFormatted,
          });
        });
      });

      if (!rows.length) {
        ElNotification.warning({
          title: "Sem dados",
          message: "Nenhum debito para exportar no mes selecionado.",
          position: "bottom-right",
        });
        return;
      }

      this.isLoadingDownloadButton = true;
      try {
        const header = [
          "Funcionario",
          "CPF",
          "Descricao",
          "Realizado em",
          "Status",
          "Valor",
        ];
        const csvLines = [header.map(escapeCsvValue).join(";")];
        rows.forEach((row) => {
          csvLines.push(
            [
              row.employeeName,
              row.employeeCpf,
              row.description,
              row.realizedAt,
              row.status,
              row.amount,
            ]
              .map(escapeCsvValue)
              .join(";")
          );
        });
        const blob = new Blob([csvLines.join("\n")], {
          type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `relatorio-debitos-funcionarios-${this.selectedMonth}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        ElNotification.success({
          title: "Relatorio gerado",
          message: "Download iniciado com sucesso.",
          position: "bottom-right",
        });
      } catch (error) {
        ElNotification.error({
          title: "Erro ao gerar relatorio",
          message: error?.message || "Tente novamente.",
          position: "bottom-right",
        });
      } finally {
        this.isLoadingDownloadButton = false;
      }
    },
  },
  watch: {
    selectedMonth() {
      this.loadReport();
    },
  },
};
</script>

<style>
.el-card {
  width: 100%;
}
.mr-8 {
  margin-right: 8px !important;
}
.summary {
  text-align: right;
  font-weight: 600;
}
.summary-black {
  color: #303133;
}
.employee-section + .employee-section {
  margin-top: 32px;
}
.status-tag {
  border-width: 1px;
}
.status-tag-opened {
  background-color: #ffffff !important;
  border-color: #dcdfe6 !important;
  color: #606266 !important;
}
.status-tag-opened.is-plain {
  background-color: #ffffff !important;
}
.status-tag-paid {
  background-color: #67c23a !important;
  border-color: #67c23a !important;
  color: #ffffff !important;
}
.status-tag-paid.is-plain {
  background-color: #67c23a !important;
}
.status-tag-canceled {
  background-color: #e6a23c !important;
  border-color: #e6a23c !important;
  color: #ffffff !important;
}
.status-tag-canceled.is-plain {
  background-color: #e6a23c !important;
}
.employee-total-row,
.overall-total-row {
  margin-top: 12px;
}
.employee-total-row .summary,
.overall-total-row .summary {
  margin-bottom: 0;
}
</style>
